import { useSyncExternalStore } from 'react';
import { logEvent, getCurrentCollection, runFor, finishRun } from './telemetry';
import { primaryTopic } from '../data/educationTaxonomy';
import { readJSON, writeJSON, subscribeKey } from '../services/deviceStore';

/*
 * The reader's own Education Center library: items they bookmarked, items they recently
 * opened, progress and test attempts. Kept on the device, and in the profile when signed in
 * (services/deviceStore syncs the keys below and announces remote changes).
 *
 * Items are stored as snapshots of the Firestore document so the shelf can render before
 * (or without) a network round trip; the Education Center swaps in the live document when
 * it has one, and drops items that no longer exist.
 */

const SAVED_KEY = 'education-saved';
const RECENT_KEY = 'education-recent';
const PROGRESS_KEY = 'education-progress';
const ATTEMPTS_KEY = 'education-attempts';
const RECENT_MAX = 8;
const PROGRESS_MAX = 200;
export const COMPLETE_AT = 0.9; // a video counts as watched past 90%

const read = (key) => { const v = readJSON(key, []); return Array.isArray(v) ? v : []; };
const write = (key, value) => { try { writeJSON(key, value); } catch { /* storage full or blocked */ } };
const readMap = (key) => { const v = readJSON(key, {}); return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; };

let saved = read(SAVED_KEY);
let recent = read(RECENT_KEY);
let progress = readMap(PROGRESS_KEY);
let attempts = readMap(ATTEMPTS_KEY);
let snapshot = { saved, recent, progress, attempts };
const listeners = new Set();
const emit = () => {
  snapshot = { saved, recent, progress, attempts };
  listeners.forEach((fn) => fn());
};
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
// Another device (or sign-in / sign-out) changed the stored copy: reload it
const reload = () => { saved = read(SAVED_KEY); recent = read(RECENT_KEY); progress = readMap(PROGRESS_KEY); attempts = readMap(ATTEMPTS_KEY); emit(); };
if (typeof window !== 'undefined') [SAVED_KEY, RECENT_KEY, PROGRESS_KEY, ATTEMPTS_KEY].forEach((k) => subscribeKey(k, reload));
const getSnapshot = () => snapshot;

// Keep only what the shelf needs; muxPlaybackId/muxStatus/downloadURL let a snapshot open directly
const strip = (item) => {
  const {
    id, title, description, fileType, category, subcategory, thumbnailURL, duration, fileSize,
    downloadURL, muxPlaybackId, muxStatus, muxDownloadURL, originalFileName, fileName, featured, uploadDate,
  } = item;
  return {
    id, title, description, fileType, category, subcategory, thumbnailURL, duration, fileSize,
    downloadURL, muxPlaybackId, muxStatus, muxDownloadURL, originalFileName, fileName, featured, uploadDate,
  };
};

export const isSaved = (id) => saved.some((s) => s.id === id);

export const toggleSaved = (item) => {
  if (!item || !item.id) return;
  const on = !isSaved(item.id);
  saved = on
    ? [{ ...strip(item), savedAt: Date.now() }, ...saved]
    : saved.filter((s) => s.id !== item.id);
  write(SAVED_KEY, saved);
  emit();
  logEvent('bookmark', { itemId: item.id, title: item.title, on });
};

/*
 * Progress, per item: { position, duration, completed, updatedAt }. Videos report position as
 * they play; anything else counts as completed the moment it is opened.
 */
const trimProgress = () => {
  const ids = Object.keys(progress);
  if (ids.length <= PROGRESS_MAX) return;
  ids.sort((a, b) => (progress[a].updatedAt || 0) - (progress[b].updatedAt || 0)).slice(0, ids.length - PROGRESS_MAX).forEach((id) => { delete progress[id]; });
};

export const progressFor = (id) => progress[id] || null;

/** Fraction watched, 0..1, or 0 when unknown */
export const progressFraction = (id) => {
  const p = progress[id];
  if (!p) return 0;
  if (p.completed) return 1;
  return p.duration ? Math.min(1, p.position / p.duration) : 0;
};

export const isCompleted = (id) => Boolean(progress[id] && progress[id].completed);

/** A video that is started but not finished: the Continue Watching case */
export const isInProgress = (id) => {
  const p = progress[id];
  return Boolean(p && !p.completed && p.duration && p.position > 5 && p.position / p.duration < COMPLETE_AT);
};

export const recordProgress = (item, position, duration) => {
  if (!item || !item.id || !(duration > 0)) return;
  const prev = progress[item.id] || {};
  const completed = prev.completed || position / duration >= COMPLETE_AT;
  progress = { ...progress, [item.id]: { position: Math.floor(position), duration: Math.floor(duration), completed, updatedAt: Date.now() } };
  trimProgress();
  write(PROGRESS_KEY, progress);
  emit();
  if (completed && !prev.completed) {
    const col = getCurrentCollection();
    logEvent('complete', { itemId: item.id, title: item.title, fileType: item.fileType, collectionId: col, runId: col ? runFor(col) : null, fraction: Math.min(1, position / duration) });
  }
};

export const markCompleted = (item) => {
  if (!item || !item.id) return;
  const prev = progress[item.id] || {};
  progress = { ...progress, [item.id]: { ...prev, completed: true, updatedAt: Date.now() } };
  trimProgress();
  write(PROGRESS_KEY, progress);
  emit();
};

export const recordOpen = (item) => {
  if (!item || !item.id) return;
  recent = [{ ...strip(item), openedAt: Date.now() }, ...recent.filter((r) => r.id !== item.id)].slice(0, RECENT_MAX);
  write(RECENT_KEY, recent);
  const col = getCurrentCollection();
  const topic = primaryTopic(item);
  logEvent('view', { itemId: item.id, title: item.title, fileType: item.fileType, topic: topic ? topic.id : null, collectionId: col, runId: col ? runFor(col) : null });
  // Opening a document or deck is as far as we can track it; call it done
  if (item.fileType !== 'video' && !(progress[item.id] && progress[item.id].completed)) {
    progress = { ...progress, [item.id]: { ...(progress[item.id] || {}), completed: true, updatedAt: Date.now() } };
    trimProgress();
    write(PROGRESS_KEY, progress);
    logEvent('complete', { itemId: item.id, title: item.title, fileType: item.fileType, collectionId: col, runId: col ? runFor(col) : null, fraction: 1 });
  }
  // Refresh the saved snapshot too, so an edited title catches up on the next open
  if (isSaved(item.id)) {
    saved = saved.map((s) => (s.id === item.id ? { ...strip(item), savedAt: s.savedAt } : s));
    write(SAVED_KEY, saved);
  }
  emit();
};

/*
 * Assessment attempts, per curriculum and test: { pre: { latest, best }, post: {...} } where each
 * record is { score, total, answers: { [questionId]: optionId }, completedAt }.
 */
export const attemptsFor = (collectionId) => attempts[collectionId] || {};

export const recordAttempt = (collectionId, which, record, meta = {}) => {
  if (!collectionId || !which || !record) return;
  const cur = attempts[collectionId] || {};
  const prev = cur[which] || {};
  const best = !prev.best || record.score > prev.best.score ? record : prev.best;
  attempts = { ...attempts, [collectionId]: { ...cur, [which]: { latest: record, best } } };
  write(ATTEMPTS_KEY, attempts);
  emit();
  // One run links a learner's pre-test, completions and post-test; the post-test closes it
  const runId = runFor(collectionId);
  logEvent('test', { collectionId, title: meta.title, which, score: record.score, total: record.total, runId });
  if (which === 'post') finishRun(collectionId);
};

/** Live view of the library; re-renders the caller when anything changes */
export const useEducationLibrary = () => {
  const { saved: s, recent: r, progress: pr, attempts: at } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    saved: s,
    recent: r,
    progress: pr,
    attempts: at,
    attemptsFor: (collectionId) => at[collectionId] || {},
    isSaved: (id) => s.some((x) => x.id === id),
    toggleSaved,
    recordOpen,
    fractionOf: (id) => (pr[id] ? (pr[id].completed ? 1 : (pr[id].duration ? Math.min(1, pr[id].position / pr[id].duration) : 0)) : 0),
    completedOf: (id) => Boolean(pr[id] && pr[id].completed),
  };
};

/**
 * What the shelf shows: bookmarks first (newest first), then recently opened items that are
 * not bookmarked. `live` is the current catalogue when loaded; it replaces snapshots and
 * removes anything deleted since.
 */
export const shelfItems = (s, r, live, max = 8) => {
  const byId = live && live.length ? new Map(live.map((i) => [i.id, i])) : null;
  const resolve = (snap) => (byId ? byId.get(snap.id) || null : snap);
  const out = [];
  const seen = new Set();
  // Half-watched videos lead the shelf: that is the thing a reader most wants back
  r.forEach((snap) => {
    const item = resolve(snap);
    if (item && !seen.has(item.id) && isInProgress(item.id)) { seen.add(item.id); out.push({ ...item, shelf: 'continue' }); }
  });
  s.forEach((snap) => {
    const item = resolve(snap);
    if (item && !seen.has(item.id)) { seen.add(item.id); out.push({ ...item, shelf: 'saved' }); }
  });
  r.forEach((snap) => {
    const item = resolve(snap);
    if (item && !seen.has(item.id)) { seen.add(item.id); out.push({ ...item, shelf: 'recent' }); }
  });
  return out.slice(0, max);
};
